import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";

import { isNonEmptyArray } from "_helpers";
import { 
    useAdminApiList,
    useAdminApiCrud,
    useAdminRemoteLicensesList,
    useAdminRemoteLicensesCrud,
    useAdminPermissionsList,
    useAdminPermissionsCrud,
    useAdminUsersList,
    useAdminUsersCrud
} from "_hooks";

import { apiColumns, remoteLicensesColumns, permissionsColumns, usersColumns } from "./__columns";
import { DrawerAddUser } from "./__DrawerAddUser";
import { TableCell } from "./__TableCell";

import { CommonActionsCell } from "./_CommonActionsCell";
import { RemoteLicensesActionsCell } from "./_RemoteLicensesActionsCell";


import { 
    Button,
    Form,
    Menu,
    message as Message, 
    Table
} from "antd";
import { useNavigate } from "react-router-dom";

export function Administration(  ){
    const [ form ] = Form.useForm();

    const defaultMenuSelected = useRef('api_keys').current;
    const menuItems = useRef([
        { key: 'api_keys', label: 'Clés API' },
        { key: 'remote_licenses', label: 'Licences distantes' },
        { key: 'permissions', label: 'Permissions' },
        { key: 'users', label: 'Utilisateurs' }
    ]).current;

    const [ apiRowData, setApiRowData ] = useState([]);
    const [ remoteLicensesRowData, setRemoteLicensesRowData ] = useState([]);
    const [ permissionsRowData, setPermissionsRowData ] = useState([]);
    const [ usersRowData, setUsersRowData ] = useState([]);

    const [ userDrawerIsOpen, setUserDrawerIsOpen ] = useState(false);

    const [ selectedMenuKey, setSelectedMenuKey ] = useState("api_keys");
    const [ editModeKey, setEditModeKey ] = useState();

    const prevSelectedMenuKey = useRef(selectedMenuKey);

    const { apiData, apiDoRedirect, fetchApiData } = useAdminApiList();
    const { setApiValues, setApiResetValues, apiSuccessMsg, apiErrorMsg } = useAdminApiCrud();

    const { remoteLicensesData, remoteLicensesDoRedirect, fetchRemoteLicensesData } = useAdminRemoteLicensesList();
    const { setRemoteLicensesValues, setRemoteLicensesResetValues, remoteLicensesSuccessMsg, remoteLicensesErrorMsg } = useAdminRemoteLicensesCrud();

    const { permissionsData, permissionsDoRedirect, fetchPermissionsData } = useAdminPermissionsList();
    const { setPermissionsValues, setPermissionsResetValues, permissionsSuccessMsg, permissionsErrorMsg } = useAdminPermissionsCrud();

    const { usersData, usersDoRedirect, fetchUsersData } = useAdminUsersList();
    const { setUsersValues, setUsersResetValues, usersSuccessMsg, usersErrorMsg } = useAdminUsersCrud();

    const [ messageApi, contextHolder ] = Message.useMessage();
    const navigate = useNavigate();

    useEffect(() => {
        setApiRowData(apiData);
    }, [apiData]);

    useEffect(() => {
        setRemoteLicensesRowData(arrayToTree(remoteLicensesData, true));
    }, [remoteLicensesData]);

    useEffect(() => {
        setPermissionsRowData(arrayToTree(permissionsData));
    }, [permissionsData]);

    useEffect(() => {
        setUsersRowData(usersData);
    }, [usersData]);

    useEffect(() => {
        if( apiDoRedirect || remoteLicensesDoRedirect || permissionsDoRedirect || usersDoRedirect ){
            navigate('/');
        }
    }, [apiDoRedirect, remoteLicensesDoRedirect, permissionsDoRedirect, usersDoRedirect]);

    const success = useCallback((message) => {
        return messageApi.success(message);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const error = useCallback((message) => {
        return messageApi.error(message);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if( apiSuccessMsg || apiErrorMsg){
            const timer = setTimeout(() => {
                if( apiSuccessMsg ){
                    success(apiSuccessMsg);
                    fetchApiData();
                } else {
                    error(apiErrorMsg);
                }
                setApiResetValues();
            }, 250);
            return () => clearTimeout(timer);
        }

        if( remoteLicensesSuccessMsg || remoteLicensesErrorMsg){
            const timer = setTimeout(() => {
                if( remoteLicensesSuccessMsg ){
                    success(remoteLicensesSuccessMsg);
                    fetchRemoteLicensesData();
                } else {
                    error(remoteLicensesErrorMsg);
                }
                setRemoteLicensesResetValues();
            }, 250);
            return () => clearTimeout(timer);
        }

        if( permissionsSuccessMsg || permissionsErrorMsg){
            const timer = setTimeout(() => {
                if( permissionsSuccessMsg ){
                    success(permissionsSuccessMsg);
                    fetchPermissionsData();
                } else {
                    error(permissionsErrorMsg);
                }
                setPermissionsResetValues();
            }, 250);
            return () => clearTimeout(timer);
        }

        if( usersSuccessMsg || usersErrorMsg){
            const timer = setTimeout(() => {
                if( usersSuccessMsg ){
                    success(usersSuccessMsg);
                    fetchUsersData();
                } else {
                    error(usersErrorMsg);
                }
                setUsersResetValues();
            }, 250);
            return () => clearTimeout(timer);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        apiSuccessMsg, 
        apiErrorMsg, 
        remoteLicensesSuccessMsg, 
        remoteLicensesErrorMsg, 
        permissionsSuccessMsg, 
        permissionsErrorMsg, 
        usersSuccessMsg, 
        usersErrorMsg
    ]);

    const isEmptyDate = d => (!d || d === '0000-00-00' || d === '0000-00-00 00:00:00');
    const sortDisabled = (a, b) => {
        if( a.is_disabled !== b.is_disabled ) {
            return a.is_disabled ? 1 : -1;
        }
    
        // 2️. Si les deux sont actifs
        if( !a.is_disabled && !b.is_disabled ) {
            const aEmpty = isEmptyDate(a.date_activation);
            const bEmpty = isEmptyDate(b.date_activation);

            // Si l’un a une date et pas l’autre
            if( aEmpty && !bEmpty ) return 1;
            if( !aEmpty && bEmpty ) return -1;
            // Trier par date_activation (croissante)
            if( !aEmpty && !bEmpty ){ 
                return new Date(a.date_activation) - new Date(b.date_activation);
            }
            return 0;
        }
    
        // 3️. Si les deux sont désactivés
        if( a.is_disabled && b.is_disabled ) {
            const aEmpty = isEmptyDate(a.date_deactivation);
            const bEmpty = isEmptyDate(b.date_deactivation);

            if( aEmpty && !bEmpty ) return 1;
            if( !aEmpty && bEmpty ) return -1;
            // Trier par date_deactivation (croissante)
            if( !aEmpty && !bEmpty ){
                return new Date(a.date_deactivation) - new Date(b.date_deactivation);
            }
            return 0;
        }

        return 0;
    }

    const isEditing = key => (key === editModeKey);

    const titleRender = ( title, align = 'center' ) => {
        let classNameExtend = "";
        switch( align ){
            case 'left' : classNameExtend = 'justify-content-start text-start'; break ;
            case 'center' : classNameExtend = 'justify-content-center text-center'; break ;
            case 'right' : classNameExtend = 'justify-content-end text-end'; break ;
            default : break ;
        }
    
        return <div className={["d-flex", classNameExtend].join(" ")}>{ title }</div>;
    }

    const arrayToTree = useCallback((arr, withSort = false, parent = 'root') => {
        let tree = arr
            .filter(item => item.parent === parent)
            .map(child => {
                let ret = {
                    ...child
                }

                const children = arrayToTree(arr, withSort, child.key);
                if( isNonEmptyArray(children) ) {
                    ret.children = children;
                }

                return ret ;
            })

        if( withSort ){
            tree.sort(sortDisabled);
        }
        
        return tree;
    }, [sortDisabled])

    const parentList = useCallback((data, onlyParentRoot = false, idProperty = 'key', parentProperty = 'parent') => (
        data
            .reduce((acc, val) => {
                const index = acc.findIndex(el => el.value === val[idProperty]);

                if( index === -1 ){
                    if( onlyParentRoot && val[parentProperty] !== 'root' ){
                        return acc;
                    } else {
                        acc.push({
                            value: val[idProperty],
                            label: val[idProperty]
                        });
                    }
                }

                return acc;
            }, [
                { value: "_empty", label: "Sélection", disabled: true }
            ])
    ), [])

    // API PART
    const apiInputCellIsDisplayed = (key, dataIndex) => {
        if( key === 'new' ) return true ;
        if( dataIndex !== 'domain' ) return true ;
        return false;
    };

    const handleAddApiKey = ( ) => {
        const newKey = 'new';
        let emptyRow = Object.fromEntries([
            ...apiTableColumns
                .filter(el => el.hasOwnProperty('dataIndex'))
                .map(el => [el.dataIndex, null]),
            ['key', newKey]
        ]);
        setApiRowData(prevVal => [...prevVal, emptyRow]);
        setEditModeKey(newKey);
    }

    const handleCancelApiKey = useCallback(( ) => {
        setApiRowData(prevVal => prevVal.filter(e => e.key !== 'new'));
        setEditModeKey(null);
    }, [setApiRowData, setEditModeKey]);

    const apiTableColumns = useMemo(() => {
        const newColumns = apiColumns.map(col => {
            const { title, dataIndex, inputType, editable, inputValues, required } = col;

            if( !editable ) return col;

            let newCol = {
                ...col,
                onCell: rec => {
                    const editing = isEditing(rec.key);
                    const display = apiInputCellIsDisplayed(rec.key, dataIndex);
                    return {
                        title,
                        dataIndex,
                        inputType,
                        inputValues,
                        required,
                        editing,
                        display
                    };
                },
            };
            return newCol;
        });

        newColumns.push({
            title: 'Actions',
            key: 'actions',
            render: (_, rec) => {
                const editIsPending = isEditing(rec.key);
                return (
                    <CommonActionsCell 
                        rec={rec}
                        editIsPending={editIsPending}
                        tableData={apiRowData}
                        onCancelRow={handleCancelApiKey}
                        setEditModeKey={setEditModeKey}
                        setValues={setApiValues}
                    />
                );
            },
        });

        return newColumns
            .map(col => {
                return {
                    ...col,
                    title: titleRender(col.title),
                    align: col.align ?? 'center'
                }
        });
    }, [
        apiColumns, 
        apiRowData, 
        isEditing, 
        handleCancelApiKey, 
        setEditModeKey, 
        setApiValues
    ])

    // REMOTE LICENSES PART
    const parentBaseKeys = remoteLicensesRowData
        .filter(el => el.parent === 'root')
        .map(el => el.base);

    const remoteLicensesReqValue = Form.useWatch('base' ?? '', form);
    const remoteLicencesInputCellIsDisplayed = (key, dataIndex) => {
        if( key !== 'new' ) return true ;
        if( ['base', 'parent'].includes(dataIndex) ) return true ;
        if( !['date_activation', 'date_deactivation'].includes(dataIndex) && parentBaseKeys.includes(remoteLicensesReqValue) ) return true ;
        return false;
    };

    const handleAddRemoteLicense = ( ) => {
        const newKey = 'new';
        let emptyRow = Object.fromEntries([
            ...remoteLicensesTableColumns
                .filter(el => el.hasOwnProperty('dataIndex'))
                .map(el => [el.dataIndex, null]),
            ['key', newKey]
        ]);
        setRemoteLicensesRowData(prevVal => [...prevVal, emptyRow]);
        setEditModeKey(newKey);
    }

    const handleCancelRemoteLicense = useCallback(( ) => {
        setRemoteLicensesRowData(prevVal => prevVal.filter(e => e.key !== 'new'));
        setEditModeKey(null);
    }, [setRemoteLicensesRowData, setEditModeKey]);

    const remoteLicensesTableColumns = useMemo(() => {
        const newColumns = remoteLicensesColumns.map(col => {
            const { title, dataIndex, inputType, editable, required } = col;
        
            let newCol = { ...col };
            if( dataIndex === 'parent' ) {
                newCol = {
                    ...col,
                    inputValues: parentList(remoteLicensesData, true)
                };
            }
        
            if( !editable ) return newCol;

            newCol = {
                ...newCol,
                onCell: rec => {
                    const editing = isEditing(rec.key);
                    const display = remoteLicencesInputCellIsDisplayed(rec.key, dataIndex);
                    return {
                        title,
                        dataIndex,
                        inputType,
                        inputValues: newCol.inputValues ?? [],
                        required,
                        editing,
                        display
                    };
                },
            };
            return newCol;
        });
        
        newColumns.push({
            title: 'Actions',
            key: 'actions',
            render: (_, rec) => {
                const isDisabled = rec.is_disabled;
                const editIsPending = isEditing(rec.key);
                const display = (rec.hasOwnProperty('parent') && rec.parent !== 'root');
                return (
                    <>
                        {
                            display 
                            &&
                                <RemoteLicensesActionsCell 
                                    rec={rec}
                                    isDisabled={isDisabled}
                                    editIsPending={editIsPending}
                                    tableData={remoteLicensesRowData}
                                    onCancelRow={handleCancelRemoteLicense}
                                    setEditModeKey={setEditModeKey}
                                    setValues={setRemoteLicensesValues}
                                />
                        }
                    </>
                );
            }
        });
      
        return newColumns
            .map(col => {
                return {
                    ...col,
                    title: titleRender(col.title),
                    align: col.align ?? 'center'
                }
        });
    }, [
        remoteLicensesColumns, 
        remoteLicensesData, 
        remoteLicensesRowData, 
        isEditing, 
        handleCancelRemoteLicense,
        setEditModeKey, 
        setRemoteLicensesValues
    ]);

    // PERMISSIONS PART
    const permissionInputCellIsDisplayed = (key, dataIndex) => {
        if( key === 'new' ) return true ;
        if( dataIndex !== 'key' ) return true ;
        return false;
    };

    const handleAddPermission = ( ) => {
        const newKey = 'new';
        let emptyRow = Object.fromEntries([
            ...permissionsTableColumns
                .filter(el => el.hasOwnProperty('dataIndex'))
                .map(el => [el.dataIndex, null]),
            ['key', newKey]
        ]);
        setPermissionsRowData(prevVal => [...prevVal, emptyRow]);
        setEditModeKey(newKey);
    }

    const handleCancelPermission = useCallback(( ) => {
        setPermissionsRowData(prevVal => prevVal.filter(e => e.key !== 'new'));
        setEditModeKey(null);
    }, [setPermissionsRowData, setEditModeKey]);

    const permissionsTableColumns = useMemo(() => {
        const newColumns = permissionsColumns.map(col => {
            const { title, dataIndex, inputType, editable, required } = col;
        
            let newCol = { ...col };
            if( dataIndex === 'parent' ) {
                newCol = {
                    ...col,
                    inputValues: parentList(permissionsData, true)
                };
            }
        
            if( !editable ) return newCol;

            newCol = {
                ...newCol,
                onCell: rec => {
                    const editing = isEditing(rec.key);
                    const display = permissionInputCellIsDisplayed(rec.key, dataIndex);
                    return {
                        title,
                        dataIndex,
                        inputType,
                        inputValues: newCol.inputValues ?? [],
                        required,
                        editing,
                        display
                    };
                },
            };
            return newCol;
        });
        
        newColumns.push({
            title: 'Actions',
            key: 'actions',
            render: (_, rec) => {
                const editIsPending = isEditing(rec.key);
                const display = (!rec.hasOwnProperty('children'));
                return (
                    <>
                        {
                            display 
                            &&
                                <CommonActionsCell 
                                    rec={rec}
                                    editIsPending={editIsPending}
                                    tableData={permissionsRowData}
                                    onCancelRow={handleCancelPermission}
                                    setEditModeKey={setEditModeKey}
                                    setValues={setPermissionsValues}
                                />
                        }
                    </>
                );
            }
        });
      
        return newColumns
            .map(col => {
                return {
                    ...col,
                    title: titleRender(col.title),
                    align: col.align ?? 'center'
                }
        });
    }, [
        permissionsColumns, 
        permissionsData, 
        permissionsRowData, 
        isEditing, 
        handleCancelPermission,
        setEditModeKey, 
        setPermissionsValues
    ]);

    // USERS PART
    const handleCancelUser = useCallback(( ) => {
        setEditModeKey(null);
    }, [setEditModeKey]);

    const usersTableColumns = useMemo(() => {
        const newColumns = usersColumns.map(col => {
            const { title, dataIndex, inputType, mode, maxTagCount, editable, required } = col;

            let newCol = { ...col };
            if( dataIndex === 'permissions' ) {
                newCol = {
                    ...col,
                    inputValues: parentList(permissionsData)
                };
            }

            if( !editable ) return col;

            newCol = {
                ...newCol,
                onCell: rec => {
                    const editing = isEditing(rec.key);
                    return {
                        title,
                        dataIndex,
                        inputType,
                        inputValues: newCol.inputValues ?? [],
                        mode,
                        maxTagCount,
                        required,
                        editing,
                        display: true
                    };
                },
            };
            return newCol;
        });

        newColumns.push({
            title: 'Actions',
            key: 'actions',
            render: (_, rec) => {
                const editIsPending = isEditing(rec.key);
                return (
                    <CommonActionsCell 
                        rec={rec}
                        editIsPending={editIsPending}
                        tableData={usersRowData}
                        onCancelRow={handleCancelUser}
                        setEditModeKey={setEditModeKey}
                        setValues={setUsersValues}
                    />
                );
            },
        });

        return newColumns
            .map(col => {
                return {
                    ...col,
                    title: titleRender(col.title),
                    align: col.align ?? 'center'
                }
        });
    }, [
        usersColumns, 
        usersRowData, 
        permissionsData,
        isEditing, 
        setEditModeKey, 
        setUsersValues
    ])

    // ********************

    useEffect(() => {
        if( prevSelectedMenuKey.current !== selectedMenuKey ){
            form.resetFields();
            handleCancelApiKey();
            handleCancelRemoteLicense();
            handleCancelPermission();
        }
        prevSelectedMenuKey.current = selectedMenuKey
    }, [selectedMenuKey]);

    const menuRender = ( ) => {
        switch( selectedMenuKey ){
            case 'api_keys' : 
                return (
                    <>
                        <Table 
                            dataSource={apiRowData}
                            columns={apiTableColumns}
                            pagination={false}
                            components={{
                                body: { cell: TableCell }
                            }}
                        />
                        <Button type="primary" className="d-flex ms-auto mt-2" onClick={handleAddApiKey}>
                            Ajouter clé API
                        </Button>
                    </>
                );
            case 'remote_licenses' :
                return (
                    <>
                        <Table 
                            key={remoteLicensesRowData.length}
                            dataSource={remoteLicensesRowData}
                            columns={remoteLicensesTableColumns}
                            pagination={false}
                            expandable={{
                                defaultExpandAllRows: true
                            }}
                            components={{
                                body: { cell: TableCell }
                            }}
                            rowClassName={(rec) =>
                                rec.is_disabled ? 'row-inactive editable-row' : 'editable-row'
                            }
                        />
                        <Button type="primary" className="d-flex ms-auto mt-2" onClick={handleAddRemoteLicense}>
                            Ajouter licence
                        </Button>

                                
                    </>
                );
            case 'permissions' :
                return (
                    <>
                        <Table 
                            key={permissionsRowData.length}
                            dataSource={permissionsRowData}
                            columns={permissionsTableColumns}
                            pagination={false}
                            expandable={{
                                defaultExpandAllRows: true
                            }}
                            components={{
                                body: { cell: TableCell }
                            }}
                        />
                        <Button type="primary" className="d-flex ms-auto mt-2" onClick={handleAddPermission}>
                            Ajouter permission
                        </Button>
                    </>
                );
            case 'users' :
                return (
                    <>
                        <Table 
                            dataSource={usersRowData}
                            columns={usersTableColumns}
                            pagination={false}
                            components={{
                                body: { cell: TableCell }
                            }}
                        />
                        <Button 
                            type="primary" 
                            className="d-flex ms-auto mt-2" 
                            style={userDrawerIsOpen ? { color: "white", backgroundColor: "grey" } : undefined} 
                            onClick={() => setUserDrawerIsOpen(prevVal => !prevVal)}
                        >
                            Ajouter un utilisateur
                        </Button>
                    </>
                );
            default : return null;
        }
    }

    return(
        <>
            { contextHolder }
            <Menu 
                items={menuItems}
                mode="horizontal"
                defaultSelectedKeys={[defaultMenuSelected]}
                onSelect={({ key }) => setSelectedMenuKey(key)}
            />
            <DrawerAddUser 
                open={userDrawerIsOpen}
                permissionsTree={permissionsRowData}
                setValues={setUsersValues}
                onClose={() => setUserDrawerIsOpen(false)}
            />
            <Form form={form} component={false}>
                { menuRender() }
            </Form>
            <div style={{ height: '12px' }}></div>
        </>
    );
}